import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Check, X, Search, MoreHorizontal, Eye, List, CalendarDays, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import TablePagination from "../components/TablePagination";
import FilterChips from "../components/FilterChips";
import BookingCalendar from "../components/BookingCalendar";
import { fmtDate, fmtDateTime as fmt } from "../lib/format";
import { AssignDialog } from "../components/AssignDialog";

const STATUS = {
  menunggu: { label: "Menunggu", cls: "bg-slate-500 hover:bg-slate-500" },
  menunggu_kelulusan: { label: "Menunggu Kelulusan", cls: "bg-amber-500 hover:bg-amber-500" },
  diluluskan: { label: "Diluluskan", cls: "bg-[#198754] hover:bg-[#198754]" },
  ditolak: { label: "Ditolak", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
  selesai: { label: "Selesai", cls: "bg-[#10056E] hover:bg-[#10056E]" },
};

const fmtTime = (dt) => {
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const durLabel = (s, e) => {
  const hours = Math.round((new Date(e) - new Date(s)) / 3600000);
  if (hours < 24) return `${hours} jam`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem ? `${days}h ${rem}j` : `${days} hari`;
};

function TempohTimeline({ start, end }) {
  return (
    <div className="flex items-center gap-2 min-w-52" data-testid="tempoh-timeline">
      <div className="text-right shrink-0">
        <p className="text-xs font-semibold leading-tight">{fmtDate(start)}</p>
        <p className="text-[11px] text-muted-foreground">{fmtTime(start)}</p>
      </div>
      <div className="flex-1 flex flex-col items-center min-w-16">
        <span className="text-[10px] font-bold text-[#10056E] leading-none mb-1">{durLabel(start, end)}</span>
        <div className="flex items-center w-full">
          <span className="h-2 w-2 rounded-full bg-[#10056E] shrink-0" />
          <span className="flex-1 h-0.5 bg-gradient-to-r from-[#10056E] to-[#198754]" />
          <span className="h-2 w-2 rounded-full bg-[#198754] shrink-0" />
        </div>
      </div>
      <div className="shrink-0">
        <p className="text-xs font-semibold leading-tight">{fmtDate(end)}</p>
        <p className="text-[11px] text-muted-foreground">{fmtTime(end)}</p>
      </div>
    </div>
  );
}

export default function Bookings() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const isPemandu = user?.role === "pemandu";
  const isAdmin = user?.role === "admin";
  const canProcess = user?.role === "operator" || user?.role === "admin";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "semua");
  const [view, setView] = useState(searchParams.get("view") === "kalendar" ? "kalendar" : "senarai");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [assign, setAssign] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [reject, setReject] = useState(null);

  const load = useCallback(() => api.get("/bookings").then((r) => setBookings(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStatusFilter(searchParams.get("status") || "semua"); }, [searchParams]);
  useEffect(() => { setView(searchParams.get("view") === "kalendar" ? "kalendar" : "senarai"); }, [searchParams]);
  useEffect(() => {
    if (searchParams.get("new") === "1") navigate("/tempahan/baharu", { replace: true });
  }, [searchParams, navigate]);
  useEffect(() => {
    const t = searchParams.get("tugaskan");
    if (t && bookings.length && canProcess) {
      const b = bookings.find((x) => x.id === t);
      if (b && b.status === "menunggu") setAssign(b);
      else if (b) navigate(`/tempahan/${b.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, searchParams]);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const filtered = bookings.filter((b) => {
    const q = search.toLowerCase();
    const matches = !q || `${b.driver_name} ${b.destination} ${b.purpose} ${b.vehicle_reg || ""}`.toLowerCase().includes(q);
    return matches && (statusFilter === "semua" || b.status === statusFilter);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const finalDecide = async (b, action) => {
    let rem = null;
    if (action === "tolak") {
      rem = window.prompt("Sebab penolakan:");
      if (rem === null) return;
    }
    try {
      await api.put(`/bookings/${b.id}/final`, { action, remarks: rem });
      toast.success(action === "lulus" ? "Kelulusan akhir diberikan — staf dimaklumkan" : "Tempahan ditolak");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doReject = async () => {
    try {
      await api.put(`/bookings/${reject.id}/reject`, { remarks });
      toast.success("Tempahan ditolak");
      setReject(null);
      setRemarks("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="bookings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tempahan</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isPemandu ? "Tugasan pemanduan anda." : isDriver ? "Mohon kenderaan untuk perjalanan rasmi anda." : `Semak dan proses permohonan tempahan — ${bookings.filter((b) => b.status === "menunggu").length} menunggu kelulusan.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center border border-slate-200 rounded-lg p-0.5 bg-white">
            <button onClick={() => setView("senarai")} data-testid="view-senarai-btn"
              className={`h-8 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-colors ${view === "senarai" ? "bg-[#10056E] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <List className="h-4 w-4" /> Senarai
            </button>
            <button onClick={() => setView("kalendar")} data-testid="view-kalendar-btn"
              className={`h-8 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-colors ${view === "kalendar" ? "bg-[#10056E] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <CalendarDays className="h-4 w-4" /> Kalendar
            </button>
          </div>
          {!isPemandu && (
          <Button className="bg-[#10056E] hover:bg-[#2a17a8]" onClick={() => navigate("/tempahan/baharu")} data-testid="new-booking-btn">
            <Plus className="h-4 w-4 mr-2" /> Tempahan Baharu
          </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <FilterChips
            value={statusFilter}
            onChange={setStatusFilter}
            testId="booking-status-chips"
            options={[
              { value: "semua", label: "Semua", count: bookings.length },
              ...Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.label, count: bookings.filter((x) => x.status === k).length })),
            ]}
          />
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari staf, destinasi..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="booking-search-input" />
          </div>
        </div>
        {view === "kalendar" ? (
          <BookingCalendar bookings={filtered} />
        ) : (
        <>
        {/* Mobile card list */}
        <div className="sm:hidden divide-y" data-testid="bookings-mobile-list">
          {paged.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Tiada tempahan dijumpai.</p>}
          {paged.map((b) => (
            <div key={b.id} className="p-4 active:bg-slate-50" onClick={() => navigate(`/tempahan/${b.id}`)} data-testid={`booking-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{b.destination}</p>
                  {!isDriver && <p className="text-xs text-muted-foreground truncate">{b.driver_name}</p>}
                </div>
                <Badge className={STATUS[b.status]?.cls}>{b.trip_status === "berjalan" ? "Berjalan" : STATUS[b.status]?.label}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{fmt(b.start_datetime)} → {fmt(b.end_datetime)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {b.vehicle_reg || "Belum ditugaskan"} · {b.estimated_distance} km · {b.passengers || 1} penumpang
              </p>
              {canProcess && b.status === "menunggu" && (
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" className="bg-[#198754] hover:bg-[#157347] h-10 flex-1" onClick={() => setAssign(b)} data-testid={`approve-booking-mobile-${b.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Tugaskan Kenderaan & Pemandu
                  </Button>
                  <Button size="sm" variant="outline" className="text-[#DC3545] border-[#DC3545] hover:bg-red-50 h-10 flex-1"
                    onClick={() => { setReject(b); setRemarks(""); }} data-testid={`reject-booking-mobile-${b.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Tolak
                  </Button>
                </div>
              )}
              {isAdmin && b.status === "menunggu_kelulusan" && (
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" className="bg-[#198754] hover:bg-[#157347] h-10 flex-1" onClick={() => finalDecide(b, "lulus")} data-testid={`final-approve-mobile-${b.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Lulus Akhir
                  </Button>
                  <Button size="sm" variant="outline" className="text-[#DC3545] border-[#DC3545] hover:bg-red-50 h-10 flex-1"
                    onClick={() => finalDecide(b, "tolak")} data-testid={`final-reject-mobile-${b.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Tolak
                  </Button>
                </div>
              )}
              {isDriver && b.status === "diluluskan" && !b.trip_logged && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" className={`w-full h-10 ${b.trip_status === "berjalan" ? "bg-[#DC3545] hover:bg-[#bb2d3b]" : "bg-[#198754] hover:bg-[#157347]"}`}
                    onClick={() => navigate(`/perjalanan/${b.id}`)} data-testid={`trip-action-mobile-${b.id}`}>
                    {b.trip_status === "berjalan" ? "Tamat Perjalanan" : "Mula Perjalanan"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              {!isDriver && <TableHead>Staf</TableHead>}
              <TableHead>Tempoh</TableHead>
              <TableHead>Destinasi</TableHead>
              <TableHead>Tujuan</TableHead>
              <TableHead>Angg. Jarak</TableHead>
              <TableHead>Kenderaan</TableHead>
              <TableHead>Dimohon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Tindakan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Tiada tempahan dijumpai.</TableCell></TableRow>
            )}
            {paged.map((b) => (
              <TableRow key={b.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/tempahan/${b.id}`)} data-testid={`booking-row-${b.id}`}>
                {!isDriver && <TableCell className="font-semibold">{b.driver_name}</TableCell>}
                <TableCell><TempohTimeline start={b.start_datetime} end={b.end_datetime} /></TableCell>
                <TableCell className="text-sm">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#10056E] shrink-0" /> {b.destination}
                  </span>
                </TableCell>
                <TableCell className="text-sm max-w-44">
                  <p className="truncate">{b.purpose}</p>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <User className="h-3 w-3 shrink-0" /> {b.passengers || 1} penumpang
                  </span>
                </TableCell>
                <TableCell className="text-sm">{b.estimated_distance} km</TableCell>
                <TableCell className="text-sm font-semibold">{b.vehicle_reg || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {fmtDate(b.created_at)}<br />
                  <span className="text-xs">{fmtTime(b.created_at)}</span>
                </TableCell>
                <TableCell><Badge className={STATUS[b.status]?.cls} data-testid={`booking-status-${b.id}`}>{STATUS[b.status]?.label}</Badge></TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end items-center gap-1.5">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`booking-actions-${b.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white w-48">
                        <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/tempahan/${b.id}`)}>
                          <Eye className="h-4 w-4 mr-2" /> Lihat Detail
                        </DropdownMenuItem>
                        {canProcess && b.status === "menunggu" && (
                          <>
                            <DropdownMenuItem className="cursor-pointer text-[#198754]" onClick={() => setAssign(b)} data-testid={`approve-booking-${b.id}`}>
                              <Check className="h-4 w-4 mr-2" /> Tugaskan Kenderaan & Pemandu
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer text-[#DC3545]" onClick={() => { setReject(b); setRemarks(""); }} data-testid={`reject-booking-${b.id}`}>
                              <X className="h-4 w-4 mr-2" /> Tolak
                            </DropdownMenuItem>
                          </>
                        )}
                        {isAdmin && b.status === "menunggu_kelulusan" && (
                          <>
                            <DropdownMenuItem className="cursor-pointer text-[#198754]" onClick={() => finalDecide(b, "lulus")} data-testid={`final-approve-${b.id}`}>
                              <Check className="h-4 w-4 mr-2" /> Lulus Akhir
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer text-[#DC3545]" onClick={() => finalDecide(b, "tolak")} data-testid={`final-reject-${b.id}`}>
                              <X className="h-4 w-4 mr-2" /> Tolak
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
        </>
        )}
      </div>

      <AssignDialog booking={assign} open={!!assign} onOpenChange={(o) => !o && setAssign(null)} isAdmin={isAdmin} onDone={load} />

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Tolak Tempahan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Sebab Penolakan</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="reject-remarks-input" placeholder="Nyatakan sebab..." />
            </div>
            <Button className="w-full bg-[#DC3545] hover:bg-[#bb2d3b]" onClick={doReject} data-testid="confirm-reject-btn">Tolak Tempahan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
